import { LightMode } from "@/components/ui/color-mode";
import { InputField } from "@/components/input-field/InputField";
import { AppColors } from "@/styles/colors";
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  MenuOpenChangeDetails,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

export interface BindAccountResponse {
  accountId: number;
  cvuId: number;
  cvuAlias: string;
  cvu: string;
  cuitCuil: string;
}

interface Props {
  open: boolean;
  onOpenChange: (e: MenuOpenChangeDetails) => void;
  loadingBind: boolean;
  loadingConfirm: boolean;
  canRetry: boolean;
  bindData: BindAccountResponse | null;
  onRetryBind: () => void;
  onCancel: () => void;
  onBack?: () => void;
  onConfirm: () => void;
}

export const ApproveModal = ({
  open,
  onOpenChange,
  loadingBind,
  loadingConfirm,
  canRetry,
  bindData,
  onRetryBind,
  onCancel,
  onBack,
  onConfirm,
}: Props) => {
  const { control, reset } = useForm({
    defaultValues: {
      cvu: "",
      alias: "",
      cuitCuil: "",
    },
  });

  useEffect(() => {
    reset({
      cvu: bindData?.cvu ?? "",
      alias: bindData?.cvuAlias ?? "",
      cuitCuil: bindData?.cuitCuil ?? "",
    });
  }, [bindData, reset]);

  const confirmDisabled =
    loadingBind ||
    loadingConfirm ||
    !bindData?.cvu?.trim()?.length ||
    !bindData?.cvuAlias?.trim()?.length ||
    !bindData?.cuitCuil?.trim()?.length;

  return (
    <Dialog.Root placement="center" lazyMount open={open} onOpenChange={onOpenChange}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content width={{ base: "calc(100vw - 32px)", md: "720px" }} maxW="720px">
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  Aprobar solicitud
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="8px" width="100%">
                <Flex flexDir="column" gap="10px">
                  {loadingBind ? (
                    <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                      Enviando solicitud a Bind...
                    </Text>
                  ) : null}

                  <Flex flexDir="column" gap="12px">
                    <Text fontSize="16px" fontWeight={600} color={AppColors.GRAY_TEXT}>
                      Datos de CVU:
                    </Text>

                    <Flex flexDir={{ base: "column", md: "row" }} gap="12px">
                      <Flex flex="1" flexDir="column" gap="6px">
                        <InputField
                          control={control as any}
                          inputName="cvu"
                          labelText="CVU"
                          disabled
                        />
                      </Flex>

                      <Flex flex="1" flexDir="column" gap="6px">
                        <InputField
                          control={control as any}
                          inputName="alias"
                          labelText="Alias"
                          disabled
                        />
                      </Flex>

                      <Flex flex="1" flexDir="column" gap="6px">
                        <InputField
                          control={control as any}
                          inputName="cuitCuil"
                          labelText="CUIT/CUIL"
                          disabled
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px">
                {onBack ? (
                  <Button variant="outline" paddingX="16px" fontWeight={600} onClick={onBack}>
                    Volver
                  </Button>
                ) : null}

                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600} onClick={onCancel}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>

                {canRetry ? (
                  <Button
                    paddingX="16px"
                    bgColor={AppColors.BLACK_TEXT_SECONDARY}
                    fontWeight={600}
                    onClick={onRetryBind}
                    loading={loadingBind}
                    disabled={loadingBind}
                  >
                    Reintentar
                  </Button>
                ) : null}

                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={onConfirm}
                  loading={loadingConfirm}
                  disabled={confirmDisabled}
                >
                  Confirmar
                </Button>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
